import {Component, OnInit, OnDestroy} from '@angular/core';
import {Sidenavaction} from '../../../../../../../assets/js/application/sidenavaction';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import * as _ from 'lodash';
import {ActivatedRoute, Router} from '@angular/router';
import * as moment from 'moment';
import {BusinessEntityService} from '../../../../../../services/businessEntity.service';
import BusinessEntity, {Address, BusinessType} from '../../../../../../services/businessEntity.model';
import {FarmSiteService} from '../../../../../../services/farmSite.service';
import FarmSiteModel, {
  BioSecurity,
  BuyerContract,
  Contract,
  ISoilType,
  IWaterType,
  SMEContract,
  IAddress,
} from '../../../../../../services/farmSite.model';
import {WaterTypeService} from '../../../../../../services/waterType.service';
import WaterTypeModel from '../../../../../../services/waterType.model';
import {SoilTypeService} from '../../../../../../services/soilType.service';
import SoilTypeModel from '../../../../../../services/soilTypeModel';
import {GeoLocationService} from '../../../../../../services/geoLocation.service';
import GeoLocationModel from '../../../../../../services/geoLocation.model';
import { CommonMethodsService } from 'src/app/services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

declare var $: any;

/**
 * @component AddFarmComponent
 * @selector app-add-farm
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `AddFarmComponent` facilitates the creation of new Farm Business Entities and their associated Farm Sites.
 * It provides a multi-step interface where users first register the business entity details (Step 1)
 * and then proceed to add specific farm site details (Step 2) including location, water/soil types, and biosecurity measures.
 *
 * ## Key Responsibilities:
 * 1. **Business Entity Registration**: Captures business details like name, email, mobile number, and address via `registerForm`.
 * 2. **Farm Site Creation**: Captures detailed farm site information including physical location, operational details, and infrastructure via `addFarmsiteForm`.
 * 3. **Location Management**: Provides cascading dropdowns/autocompletes for Country, State, and Village selection with geocoding support.
 * 4. **Contract Management**: Automatically creates a contract linking the new farm to the logged-in provider (if applicable).
 * 5. **Validation**: Performs extensive validation on form fields including patterns for names, numbers, and logical checks (e.g., water spread area vs total area).
 *
 * ## User Roles & Permissions:
 * - **Admin/Business Admin**: Typically used by administrators to onboard new farms into the system.
 * - **Provider**: Used by providers to register client farms under their management.
 *
 * ## Data Flow:
 * 1. **Initialization**: Loads reference data (Water Types, Soil Types, Countries) in `ngOnInit`.
 * 2. **Step 1 (Business)**: User fills `registerForm`. `addFarmDetailsSubmit()` creates the `BusinessEntity`.
 * 3. **Transition**: Upon success, `isFarmAddedtoDB` becomes true, enabling the Farm Site form.
 * 4. **Step 2 (Farm Site)**: User fills `addFarmsiteForm`. `addFarmSite()` creates the `FarmSiteModel` linked to the created Business ID.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: Used to determine the user's role context.
 * - `businessID`: Used for contract association (linking the new farm to the logged-in provider).
 *
 * @service BusinessEntityService - For creating the business entity.
 * @service FarmSiteService - For creating the farm site.
 * @service WaterTypeService - Fetches water types reference data.
 * @service SoilTypeService - Fetches soil types reference data.
 * @service CommonMethodsService - For error handling and alerts.
 */
@Component({
  selector: 'app-add-farm',
  templateUrl: './add-farm.component.html',
  styleUrls: ['./add-farm.component.scss']
})
export class AddFarmComponent implements OnInit, OnDestroy{
  /** @type {BusinessEntity} Model for the business entity being created. */
  public businessEntity: BusinessEntity;

  /** @type {Address} Address model for the business entity. */
  public address: Address;

  /** @type {BusinessType} Business type model (e.g., Farm). */
  public businessType: BusinessType;

  /** @type {FarmSiteModel} Model for the farm site being created. */
  public farmSite: FarmSiteModel;

  /** @type {BioSecurity} Bio-security measures model for the farm site. */
  public bioSecurity: BioSecurity;

  /** @type {Contract} Contract model for provider relationships. */
  public contract: Contract;

  /** @type {SMEContract} SME contract model (unused in current flow). */
  public smeContract: SMEContract;

  /** @type {BuyerContract} Buyer contract model (unused in current flow). */
  public buyerContract: BuyerContract;

  /** @type {IWaterType} Water type model for the farm site. */
  public waterType: IWaterType;

  /** @type {ISoilType} Soil type model for the farm site. */
  public soilType: ISoilType;

  /** @type {IAddress} Address model specifically for the farm site. */
  public farmSiteAddress: IAddress;

  /** @type {Array<WaterTypeModel>} List of available water types for dropdown. */
  public waterTypes: Array<WaterTypeModel>;

  /** @type {Array<SoilTypeModel>} List of available soil types for dropdown. */
  public soilTypes: Array<SoilTypeModel>;

  /** @type {Array<GeoLocationModel>} List of geo-locations (unused). */
  public geoLocations: Array<GeoLocationModel>;

  /** @type {FormGroup} Reactive form for business registration (Step 1). */
  public registerForm: FormGroup;

  /** @type {string} Email address input. */
  public email: string;

  /** @type {boolean} Flag to show/hide the farm site form. */
  public showsiteform: boolean;

  /** @type {boolean} Flag indicating if the form has been submitted. */
  public submitted: boolean;

  /** @type {boolean} Flag indicating if the business entity has been successfully created. */
  public isFarmAddedtoDB: boolean;

  /** @type {string} ID of the created farm business. */
  public farmbusinessid: string;

  /** @type {Date} Start date for the contract. */
  public startDate: Date;

  /** @type {Date} End date for the contract. */
  public endDate: Date;

  /** @type {string} Village name input. */
  public village: string;

  /** @type {string} Mandal name input. */
  public mandal: string;

  /** @type {string} Taluk name input. */
  public taluk: string;

  /** @type {number} Pincode input. */
  public pincode: number;

  // AFrmsite declarations
  // Validation form declaration
  /** @type {FormGroup} Reactive form for farm site creation (Step 2). */
  public addFarmsiteForm: FormGroup;

  /** @type {boolean} Flag indicating if the farm site form has been submitted. */
  public farmSitesubmitted = false;
  // end Validation form declaration
  /** @type {Array<any>} List of villages (unused). */
  public villageList = new Array();

  /** @type {boolean} Flag for Bio Fencing availability. */
  public bioFencing: boolean;

  /** @type {boolean} Flag for Hand Wash and Footbath Area availability. */
  public bioHWandFA: boolean;

  /** @type {boolean} Flag for Crab Fencing availability. */
  public bioCrabFencing: boolean;

  /** @type {boolean} Flag for Reservoirs availability. */
  public bioReservoirs: boolean;

  /** @type {boolean} Flag for Bird Fencing availability. */
  public bioBirdFencing: boolean;

  /** @type {boolean} Flag for Bio Filters availability. */
  public bioFilters: boolean;

  /** @type {Array<any>} Array of years for 'Year Started' dropdown. */
  public yearStartedArr = new Array();

  /** @type {string} Minimum start date for date picker. */
  public minstartdate: string;

  /** @type {string} Minimum end date for date picker. */
  public minenddate: string;

  /** @type {string} Maximum start date for date picker. */
  public maxstartdate: string;

  /** @type {boolean} Validation flag for water spread area vs total area check. */
  public waterareavalidation: boolean;

  /** @type {string} Logged-in user role. */
  public loginPersonType: string;

  /** @type {string} Selected item (generic). */
  public item: string;

  /** @type {string} Search keyword for autocomplete. */
  public keyword: string;

  /** @type {any} List of countries for dropdown. */
  public countries: any;

  /** @type {BusinessEntity} Pincode validation result (unused). */
  public pinCodes: BusinessEntity;

  /** @type {string} Country code of the created business. */
  public countryCode: string;

  /** @type {Array<any>} List of states for dropdown. */
  public states: Array<any>;

  /** @type {Array<any>} List of villages for dropdown. */
  public villages: Array<any>;

  /** @type {Array<any>} Filtered list of states for autocomplete. */
  public filteredStates = new Array();

  /** @type {Array<any>} Filtered list of villages for autocomplete. */
  public filteredVillages = new Array();

  /** @type {Object} Selected state item. */
  public stateitem: Object;

  /** @type {Object} Selected village item. */
  public villageItem: Object;

  /** @type {string} Keyword for state search. */
  public statekeyword: string;

  /** @type {string} Keyword for village search. */
  public villagekeyword: string;

  /** @type {FarmSiteModel} Lat/Long data fetched from geocoding service. */
  public latLongs: FarmSiteModel;

  /** @type {boolean} Flag indicating if village selection changed. */
  public villageChanged: boolean = false;

  /** @type {boolean} Flag to disable state/village fields. */
  public stateVillageDisabled: boolean = false;

  /**
   * Constructor for AddFarmComponent.
   * Initializes services and reactive forms.
   *
   * @param {Router} router - Angular Router.
   * @param {ActivatedRoute} route - Active route service.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSiteService - Service for farm site operations.
   * @param {WaterTypeService} waterTypeService - Service for water types.
   * @param {SoilTypeService} soilTypeService - Service for soil types.
   * @param {GeoLocationService} geoLocationService - Service for geo-location.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   */
  constructor(public router: Router, public route: ActivatedRoute, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public businessEntityService: BusinessEntityService, public farmSiteService: FarmSiteService, public waterTypeService: WaterTypeService, public soilTypeService: SoilTypeService, public geoLocationService: GeoLocationService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.businessEntity = new BusinessEntity();
    this.businessType = new BusinessType();
    this.address = new Address();
    this.farmSiteAddress = new IAddress();
    this.businessType = new BusinessType();
    this.farmSite = new FarmSiteModel();
    this.bioSecurity = new BioSecurity();
    this.contract = new Contract();
    this.bioSecurity = new BioSecurity();
    this.contract = new Contract();
    this.smeContract = new SMEContract();
    this.buyerContract = new BuyerContract();
    this.waterType = new IWaterType();
    this.soilType = new ISoilType();
    this.villageList = new Array();
    // Add frm Validations
    this.registerForm = this.formBuilder.group({
      inputhouseNo: ['', [Validators.required,
        Validators.pattern('^[ A-Za-z0-9.,#/-]*$'),
        Validators.minLength(3),
        Validators.maxLength(30)
      ]],
      businessName: ['', [Validators.required,
        Validators.minLength(3),
        Validators.maxLength(300),
        Validators.pattern('^[a-zA-Z0-9 .,()]+$'),
      ]],
      email: ['', [Validators.required,
        Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,6}$')
      ]],
      businessNumber: ['', [Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
        Validators.pattern('^[0-9]{10,10}$')]],
      inputpincode: ['', [Validators.required,
        Validators.minLength(5),
        Validators.maxLength(6),
        Validators.pattern('^[0-9]{5,6}$')
      ]],
      inputstreet: ['', [Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
        Validators.pattern('^[ A-Za-z0-9.,/]*$')
      ]],
      inputcity: ['', [Validators.required,
        Validators.minLength(3),
        Validators.maxLength(300),
        Validators.pattern('^[a-zA-Z ,]*$')
      ]],
      inputDistrict: ['', [Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
        Validators.pattern('^[a-zA-Z ,]*$')
      ]],
      inputState: ['', [
        Validators.minLength(3),
        Validators.maxLength(50),
        Validators.pattern('^[a-zA-Z ]*$')
      ]],
      caaid: ['', [Validators.maxLength(50)]],
      mpedaid: ['', [Validators.maxLength(50)]],
      validThrough: [null],
      countryName: ['', [Validators.required]],
    });
    // End Add frm Validations
    // validation for farm site
    this.addFarmsiteForm = this.formBuilder.group({
      farmNameAtLocation: ['', [Validators.required,
        Validators.pattern('^[a-zA-Z ]*$')]],
      yearStarted: ['', Validators.required],
      waterTypeId: ['', Validators.required],
      soilTypeId: ['', Validators.required],
      ownership: [null],
      pincode: [[Validators.required,
        Validators.pattern('^[0-9]{5,6}$')
      ]],
      village: ['', Validators.required],
      // Taluka: ['', Validators.required],
      // Mandal: ['', Validators.required],
      State: ['', Validators.required],
      // district: ['', Validators.required],
      totalFarmArea: [null],
      startDate: [null],
      endDate: [null],
      enablefp: [null],
      numberOfGrowoutPonds: [null],
      waterSpreadArea: [null],
      numberOfNurseries: [null],
      bioFencing: [null],
      bioHWandFA: [null],
      bioCrabFencing: [null],
      bioReservoirs: [null],
      bioBirdFencing: [null],
      bioFilters: [null],
      ETSOnPremises: [null],
      labOnPremises: [null]

    });
    // End validation for farm site
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Initializes date pickers and year dropdowns.
   * - Fetches reference data (Water Types, Soil Types).
   * - Sets up side navigation.
   */
  public async ngOnInit() {
    this.keyword = 'name';
    this.statekeyword = 'name';
    this.villagekeyword = 'name';
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // get persontype
      this.loginPersonType = localStorage.getItem('loginPersonType');
      // Business Details registraion validation
      this.sidenavaction.x_navigation();
      // date pickers initialization
      this.minstartdate = moment(new Date()).format('YYYY-MM-DD');
      this.minenddate = moment(new Date()).format('YYYY-MM-DD');
      this.route.params.subscribe(params => {
        this.farmbusinessid = params.id;
      });
      this.villageList = [];
      this.geoLocations = new Array();
      // Business Details registraion validation
      this.sidenavaction.x_navigation();
      // get year started
      this.yearStartedArr = new Array();
      let curryear = new Date().getFullYear();
      for (let i = 1947; i < curryear; i++) {
        this.yearStartedArr.push(i + 1);
      }
      // end get year started
      /*water types*/
      this.waterTypes = await this.waterTypeService.getAllWaterTypes();
      /*soil type*/
      this.soilTypes = await this.soilTypeService.getAllSoilTypes();
      this.validThrough();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for WaterTypeService and SoilTypeService.
   */
  public ngOnDestroy(): void {
    this.waterTypeService.cancelOnGoingHttpRequests();
    this.soilTypeService.cancelOnGoingHttpRequests();
  }

  /**
   * @method f
   * @description Getter for easy access to `registerForm` controls in the template.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method f1
   * @description Getter for easy access to `addFarmsiteForm` controls in the template.
   * @returns {AbstractControl} Form controls.
   */
  public get f1() {
    return this.addFarmsiteForm.controls;
  }

  /**
   * @method selectEvent
   * @async
   * @description Handles country selection from autocomplete.
   * Sets the country in the address model and fetches states for that country.
   * @param {any} item - The selected country object.
   */
  public async selectEvent(item: any) {
    // do something with selected item
    this.address.country = item.name;
    if (this.address.country) {
       this.getStates();
    }
  }

  /**
   * @method onChangeSearch
   * @async
   * @description Placeholder for search input change event.
   */
  public async onChangeSearch() {
    // fetch remote data from here
    // And reassign the 'data' which is binded to 'data' property.
  }

  /**
   * @method onFocused
   * @async
   * @description Placeholder for input focus event.
   */
  public async onFocused() {
    // // do something when input is focused
  }

  /**
   * @method addFarmDetailsSubmit
   * @async
   * @description Submits the business entity registration form (Step 1).
   * - Validates the form.
   * - Sets business type to 'Farm'.
   * - Calls `businessEntityService.create` to save the business.
   * - Enables the farm site creation step upon success.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async addFarmDetailsSubmit() {
    try {
      this.submitted = true;
      this.isFarmAddedtoDB = false;
      // stop here if form is invalid
      if (this.registerForm.invalid) {
        return;
      }
      this.businessEntity.email = this.businessEntity.email.toLowerCase();
      this.businessEntity.businessType = [
        {
          name: 'Farm'
        }
      ];
      // validthrough
      let validthrough = $('.validthrough').val();
      if (validthrough != '') {
        validthrough = new Date(validthrough);
        validthrough = (validthrough.getFullYear() > 0 ? validthrough.getFullYear() : validthrough.getFullYear() * -1) + '-' + (validthrough.getMonth() + 1) + '-' + validthrough.getDate();
      }
      if (validthrough) {
        this.businessEntity.validThrough = validthrough;
      }
      this.businessEntity.address.push(this.address);
      this.businessEntity = await this.businessEntityService.create(this.businessEntity);
      alert('Business created successfully');
      this.countryCode = this.businessEntity.address[0].countryCode;
      this.businessEntity.validThrough = moment(this.businessEntity.validThrough).format('DD-MMM-YYYY');
      this.submitted = false;
      this.isFarmAddedtoDB = true;
      this.villageChanged = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method addFarmSite
   * @async
   * @description Submits the farm site creation form (Step 2).
   * - Validates the form.
   * - Maps bio-security checkboxes to the model.
   * - Creates a contract linking the farm to the provider.
   * - Calls `farmSiteService.create` to save the farm site.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async addFarmSite() {
    try {
      this.farmSitesubmitted = true;
      // stop here if form is invalid
      if (this.addFarmsiteForm.invalid) {
        return;
      }
      this.farmSite.bioSecurity = [];
      if (this.bioFencing) {
        this.farmSite.bioSecurity.push({'name': 'Fencing'});
      }
      if (this.bioHWandFA) {
        this.farmSite.bioSecurity.push({'name': 'Hand Wash and Footbath Area'});
      }
      if (this.bioCrabFencing) {
        this.farmSite.bioSecurity.push({'name': 'Crab Fencing'});
      }
      if (this.bioReservoirs) {
        this.farmSite.bioSecurity.push({'name': 'Reservoirs'});
      }
      if (this.bioBirdFencing) {
        this.farmSite.bioSecurity.push({'name': 'Bird Fencing'});
      }
      if (this.bioFilters) {
        this.farmSite.bioSecurity.push({'name': 'Filters'});
      }
      if (this.waterType.waterTypeId && _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)])) {
        const watertypedata = _.filter(this.waterTypes, ['id', Number(this.waterType.waterTypeId)]);
        this.waterType.name = watertypedata[0].name;
      }
      if (this.soilType.soilTypeId && _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)])) {
        const soiltypedata = _.filter(this.soilTypes, ['id', Number(this.soilType.soilTypeId)]);
        this.soilType.name = soiltypedata[0].name;
      }
      this.farmSite.waterType = this.waterType;
      this.farmSite.soilType = this.soilType;
      this.farmSite.businessID = this.businessEntity.id;
      this.farmSite.contract = [
        {
          feedProviderID: localStorage.businessID,
          startDate: this.startDate,
          endDate: this.endDate
        }
      ];
      /*address*/
      if (this.villageChanged == true) {
        this.farmSite.address = {
          village: this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.village,
          taluk: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[1] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.state),
          state: this.farmSiteAddress.state,
          pincode: this.farmSiteAddress.pincode,
          mandal: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[1] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[0] : this.farmSiteAddress.state),
          district: this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 2 ? this.farmSiteAddress.village.split(',')[2] : (this.farmSiteAddress.village.toLowerCase().includes(',') && (this.farmSiteAddress.village.match(/,/g)).length == 1 ? this.farmSiteAddress.village.split(',')[1] : this.farmSiteAddress.state),
          latitude: this.farmSiteAddress.latitude,
          longitude: this.farmSiteAddress.longitude,
        };
      }
      this.farmSite.SMEContract = undefined;
      this.farmSite.buyerContract = undefined;
      this.farmSite = await this.farmSiteService.create(this.farmSite);
      alert('Farmsite created successfully');
      this.villageChanged = false;
      this.showsiteform = false;
      this.farmSite = new FarmSiteModel();
      this.businessEntity = new BusinessEntity();
      this.farmSiteAddress =  new IAddress();
      this.address =  new Address();
      this.submitted = false;
      this.farmSitesubmitted = false;
      this.item = undefined;
      this.stateitem = undefined;
      this.villageItem = undefined;
      this.filteredStates = new Array();
      this.filteredVillages = new Array();
      this.stateVillageDisabled = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method changewaterspreadare
   * @description Validates that the water spread area is not greater than the total farm area.
   * Sets `waterareavalidation` flag.
   */
  public changewaterspreadare() {
    if ((this.farmSite.waterSpreadArea) >= (this.farmSite.totalFarmArea)) {
      this.waterareavalidation = true;
    } else {
      this.waterareavalidation = false;
    }
  }

  /**
   * @method startdatechange
   * @description Updates the minimum end date based on the selected start date.
   */
  public startdatechange() {
    this.minenddate = moment(this.startDate).format('YYYY-MM-DD');

  }

  /**
   * @method enddatechange
   * @description Updates the maximum start date based on the selected end date.
   */
  public enddatechange() {
    this.maxstartdate = moment(this.endDate).format('YYYY-MM-DD');
  }

  /**
   * @method showsiteformClick
   * @description Toggles the visibility of the farm site form.
   * Prompts user for confirmation before showing the form.
   */
  public showsiteformClick() {
    const isadd = confirm('Are you sure you want to add farm site');
    this.isFarmAddedtoDB = false; // If the user cancel button add site button is disabled
    if (isadd) {
      this.showsiteform = true;
      this.villageChanged = false;
    } else {
      this.router.navigate(['/BusinessEntityFarmsProvider', localStorage.businessID]);
    }
  }

  /**
   * @method validThrough
   * @description Initializes the jQuery date picker for the 'valid through' date field.
   * Sets up onSelect callback to update the form control.
   */
  public validThrough() {
    let strArray = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let contract_dates = $('.validthrough').datepicker({
      minDate: new Date(),
      onSelect: function(selectedDate) {
        let parsedDate = (selectedDate.split('/')[1] + '-' + strArray[(parseInt(selectedDate.split('/')[0]) - 1)] + '-' + selectedDate.split('/')[2]);
        let option = this.id == 'validthrough' ? 'minDate' : 'maxDate',
          instance = $(this).data('datepicker'),
          date = $.datepicker.parseDate(instance.settings.dateFormat || $.datepicker._defaults.dateFormat, selectedDate, instance.settings);
        contract_dates.not(this).datepicker('option', option, date);
        $('.validthrough').val(parsedDate);
      }
    });
  }

  /**
   * @method getStates
   * @description Filters the list of states based on the selected country.
   * Handles special cases for specific countries where state selection might be disabled or pre-filled.
   */
  public getStates() {
    if (this.address.country == 'India' || this.address.country == 'Sri Lanka' || this.address.country == 'Thailand' || this.address.country == 'Ecuador') {
      this.stateitem = undefined;
      this.villageItem = undefined;
      this.farmSiteAddress.state = undefined;
      this.farmSiteAddress.village = undefined;
      this.filteredStates = _.filter(this.states, (o: any) => {
        return o.country_name == this.address.country;
      });
    } else {
      this.stateitem = this.address.country;
      this.villageItem = this.address.country;
      this.farmSiteAddress.state = this.address.country;
      this.farmSiteAddress.village = this.address.country;
      this.stateVillageDisabled = true;
    }
  }

  /**
   * @method stateSelectEvent
   * @async
   * @description Handles state selection from autocomplete.
   * Triggers fetching of villages for the selected state.
   * @param {any} item - The selected state object.
   */
  public async stateSelectEvent(item: any) {
    // do something with selected item
    this.farmSiteAddress.state = item.name;
    if(this.farmSiteAddress.state){
      this.getVillages();
    }
  }

  /**
   * @method statOnChangeSearch
   * @async
   * @description Clears village selection when state search input changes.
   */
  public async statOnChangeSearch() {
    // fetch remote data from here
    // And reassign the 'data' which is binded to 'data' property.
    this.villageItem = undefined;
    this.filteredVillages = [];
  }

  /**
   * @method stateOnFocused
   * @async
   * @description Placeholder for state input focus event.
   */
  public async stateOnFocused() {
    // // do something when input is focused
  }

  /**
   * @method stateOnCleared
   * @description Clears village selection when state input is cleared.
   */
  public stateOnCleared() {
     this.villageItem = undefined;
     this.farmSiteAddress.village = undefined;
     this.filteredVillages = [];
  }
  
  /**
   * @method getVillages
   * @async
   * @description Fetches villages for the selected state.
   * Populates `filteredVillages` with a subset of villages (limit 80).
   */
  public async getVillages() {
    this.filteredVillages = [];
    let villagesData = this.villages.filter(obj => obj.state === this.farmSiteAddress.state);
    if (villagesData && villagesData.length > 0 && villagesData[0].villages.length > 0) {
      for (let i = 0; i <= 80 ; i++) {
        let eachObj = {'name':villagesData[0].villages[i]};
        if (eachObj.name != undefined) {
          this.filteredVillages.push(eachObj);
        }
      }
    }
  }
  
  /**
   * @method villageSelectEvent
   * @async
   * @description Handles village selection from autocomplete.
   * Triggers geocoding (`checkPincode`) to fetch lat/long for the selected village.
   * @param {any} item - The selected village object.
   */
  public async villageSelectEvent(item: any) {
    // do something with selected item
    this.villageChanged = true;
    this.farmSiteAddress.village = item.name;
    if (this.farmSiteAddress.village) {
      this.checkPincode(this.farmSiteAddress.village, this.farmSiteAddress.state,this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[1] : '', this.farmSiteAddress.village.toLowerCase().includes(',') ? this.farmSiteAddress.village.split(',')[2] : '');
    }
  }

  /**
   * @method villageOnChangeSearch
   * @async
   * @description Placeholder for village search input change event.
   */
  public async villageOnChangeSearch() {
    // fetch remote data from here
    // And reassign the 'data' which is binded to 'data' property.
  }

  /**
   * @method villageOnFocused
   * @async
   * @description Placeholder for village input focus event.
   */
  public async villageOnFocused() {
    // // do something when input is focused
  }

  /**
   * @method villageOnCleared
   * @description Clears village selection and re-fetches default village list.
   */
  public villageOnCleared() {
     this.farmSiteAddress.village = undefined;
     this.getVillages();
  }

  /**
   * @method villageSearchEvent
   * @description Filters the village list based on user input.
   * @param {string} value - The search string.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  public villageSearchEvent(value: string,event: KeyboardEvent) {
    const searchData = value.toLowerCase();
    this.filteredVillages = [];
    let villagesData = this.villages.filter(obj => obj.state === this.farmSiteAddress.state);
    if (villagesData) {
      for (let i=0;i < villagesData[0].villages.length;i++) {
        let eachObj = {'name':villagesData[0].villages[i]};
        this.filteredVillages.push(eachObj);
      }
    }
    if (searchData && (event.key == 'Backspace' || event.key != 'Backspace')) {
      let filteredData = new Array();
      filteredData = this.filteredVillages.sort().filter(obj => {
        for (let j = 0;j < obj.name.length;j++) {
          if (obj.name[j].toLowerCase() == searchData[j]) {
            return true;
          }
        }
      });
      if (filteredData) {
        var check = new Set();
        this.filteredVillages = filteredData.filter(x => !check.has(x.name) && check.add(x.name));
      }
    } else if (!searchData){
      this.getVillages();
    }
  }

  /**
   * @method checkPincode
   * @async
   * @description Fetches latitude and longitude for the given location details using `FarmSiteService`.
   * @param {string} village - Village name.
   * @param {string} state - State name.
   * @param {string} mandal - Mandal name.
   * @param {string} district - District name.
   */
  public async checkPincode(village: string,state: string,mandal: string,district: string) {
    try {
      this.latLongs = await this.farmSiteService.latLongsData(village,state,mandal,district);
      if (this.latLongs.results.length > 0) {
        this.farmSiteAddress.latitude = this.latLongs.results[0].geometry.location.lat;
        this.farmSiteAddress.longitude = this.latLongs.results[0].geometry.location.lng;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }
}
